/**
 * @file The one rule that says whether a retry may be pressed. Consumers:
 * `SignifyFetchJobProgress`, for each failed row's own button, and
 * `SignifyFetchJobProgressActions`, for the footer button that carries the picks.
 * Both press the same callable, so both obey the same rule and read the same words. A running fetch
 * is NOT a reason: `retrySignifyFetchJob` resets parts a live dispatch then picks up, and the
 * dispatch reads `remaining` inside its closing transaction, so it re-enqueues rather than
 * committing `completed` over the reset. A cancel in flight is not a reason either — the callable
 * re-reads `phase` transactionally, and retrying a cancelled job is how a resume is spelled.
 * `retryable` is what the press would reset — one for a row's own button, the failed parts plus the
 * unsent picks for the footer's. A `stalled` job is retryable with nothing failed, because the retry
 * enqueues unconditionally and is the only way to reclaim parts orphaned in `running`.
 */

import type { SignifyFetchJobProgress } from './deriveSignifyFetchJobProgress';

export const signifyFetchJobRetryDisabledReason = ({
  retrying,
  retryable,
  status,
}: {
  retrying: boolean;
  retryable: number;
  status: SignifyFetchJobProgress['status'];
}): string | null =>
  retrying
    ? 'A retry is already running'
    : retryable === 0 && status !== 'stalled'
      ? 'Nothing to retry'
      : null;
